import type { BomboraConnections } from './BomboraConnections'
import { Contacts } from './Contacts'
import type { IWorkflowBaseLead } from './Lead'
import { OUTREACH_CONNECTION, type OutreachConnection } from './Outreach'

import EType = Contacts.EType

export enum EDeliveryType {
  Realtime = 'realtime',
  Scheduled = 'scheduled',
  Delayed = 'delayed',
}

export enum EDeliveryInterval {
  Day = 'day',
  Week = 'week',
  Month = 'month',
}

export enum EWorkflowTypes {
  email = 'email',
  zapier = 'zapier',
  webhook = 'webhook',
  pipedrive = 'pipedrive',
  slack = 'slack',
  hubspot = 'hubspot',
  salesforce = 'salesforce',
  microsoftTeams = 'microsoft_teams',
  googleSheets = 'google_sheets',
  linkedin = 'linkedin',
  outreach = 'outreach',
  clay = 'clay',
  attio = 'attio',
  n8n = 'n8n',
}

export enum CrmWorkflowType {
  pipedrive = 'pipedrive',
  hubspot = 'hubspot',
  salesforce = 'salesforce',
  attio = 'attio',
}

export const WORKFLOW_STATUSES = ['active', 'paused', 'stopped'] as const

export type WorkflowStatus = (typeof WORKFLOW_STATUSES)[number]

export type WorkflowFieldScope = 'lead' | 'contact'

export type WorkflowFieldName =
  // Company
  | 'address'
  | 'city'
  | 'country_iso'
  | 'country'
  | 'crunchbase'
  | 'description'
  | 'employees_from'
  | 'employees_to'
  | 'employees'
  | 'facebook'
  | 'financial_report_from'
  | 'financial_report_to'
  | 'financial_report'
  | 'founded_at'
  | 'industry_category'
  | 'linkedin'
  | 'nace_category'
  | 'nace_code'
  | 'name'
  | 'number'
  | 'phone_number'
  | 'state'
  | 'state_code'
  | 'street'
  | 'total_funding'
  | 'twitter'
  | 'website'
  | 'zip_code'
  | 'commercial_register_code'
  | 'product'
  | 'vendor'
  | 'tags'
  // Contact
  | 'contact_country_iso'
  | 'contact_country'
  | 'department'
  | 'seniority'
  | 'email'
  | 'first_name'
  | 'full_name'
  | 'last_name'
  | 'linkedin_profile'
  | 'position'
  | 'twitter_profile'
  // Albacross
  | 'contacts_url'
  | 'custom_text'
  | 'profile_url'
  | 'segment_name'
  | 'source'
  // Activity
  | 'duration_30_days'
  | 'duration_7_days'
  | 'duration_90_days'
  | 'gclids'
  | 'latest_visit'
  | 'page_views_30_days'
  | 'page_views_7_days'
  | 'page_views_90_days'
  | 'pages'
  | 'pages_high_intent'
  | 'score'
  | 'sources'
  | 'utms_campaigns'
  | 'utms_contents'
  | 'utms_mediums'
  | 'utms_sources'
  | 'utms_terms'
  | 'visits_30_days'
  | 'visits_7_days'
  | 'visits_90_days'
  | 'visitors_30_days'
  | 'visitors_7_days'
  | 'visitors_90_days'
  // optional formatting
  | 'duration'
  | 'visits'
  | 'visitors'
  | 'page_views'
  | 'unique_pages_visited'

export enum EOperationType {
  Add = 'add',
  Update = 'update',
  AddOrUpdate = 'add_or_update',
}

export enum EChannelType {
  PublicChannel = 'public_channel',
  PrivateChannel = 'private_channel',
  DirectMessage = 'im',
  GroupDirectMessage = 'mpim',
}

interface IWorkflowOutputSlackUsers {
  direct_message: true
  users: string[]
}

interface IWorkflowOutputSlackChannel {
  direct_message: false
  channel: {
    id: string
    type: EChannelType
  }
}

interface IWorkflowOutputSetup {
  setup?: {
    limitOnePerDay?: boolean
  }
}

type IWorkflowOutputFormat =
  | {
      custom_format: true
      custom_format_text: string
    }
  | {
      custom_format: false
      company_optional_fields?: string[]
    }

export type IWorkflowOutputSlack = IWorkflowOutputFormat &
  (IWorkflowOutputSlackChannel | IWorkflowOutputSlackUsers) &
  IWorkflowOutputSetup

export type EmailWorkflowReportType = 'csv' | 'default' | 'excel'

export interface IWorkflowOutputMicrosoftTeams {
  team_id: string
  channel_id: string
  company_optional_fields: string[]
}

export interface IWorkflowCsvOutputEmailReport {
  fields: string[]
  report_type: 'csv'
}

export interface IWorkflowOutputEmailReport {
  report_type: 'default'
}

export interface IWorkflowExcelOutputEmailReport {
  fields: string[]
  report_type: 'excel'
}

export type IWorkflowOutputEmail = {
  users: string[]
} & (IWorkflowCsvOutputEmailReport | IWorkflowOutputEmailReport | IWorkflowExcelOutputEmailReport)

export interface IWorkflowOutputCrmPicklistItem {
  key: string
  label: string
}

export interface IWorkflowOutputCrmPicklist {
  key: IWorkflowOutputCrmPicklistItem
  value: IWorkflowOutputCrmPicklistItem
  override?: boolean
}

export type FieldTimeUnits = 's' | 'm' | 'h' | 'auto' | 'time'
export type FieldDayPeriods = 7 | 30 | 90

export interface IWorkflowOutputCrmFieldItemFormat {
  time_unit?: FieldTimeUnits
  day_period?: FieldDayPeriods
}

export interface IWorkflowOutputCrmFieldItem<T = string> {
  key: T
  label: string
  type?: string
  custom?: boolean
  value?: string
  format?: IWorkflowOutputCrmFieldItemFormat
}

export interface IWorkflowOutputCrmField {
  required?: boolean
  override?: boolean
  editable?: boolean
  crm_field: IWorkflowOutputCrmFieldItem
  albacross_field: IWorkflowOutputCrmFieldItem<WorkflowFieldName>
  blank_field_as_match?: boolean
}

export interface IWorkflowOutputContacts {
  limit: number
  type: EType
  keywords: string[]
  seniority: string[]
  department: string[]
  with_emails: boolean
  must_have_contacts: boolean
  with_phone_numbers: boolean
  with_linkedin_url?: boolean
  skip_filters_if_no_matches: boolean
  phone_number?: boolean
}

export interface IWorkflowOutputN8n {
  webhook_url: string
  contacts?: IWorkflowOutputContacts
}

export type IWorkflowModelFields = {
  custom_fields: IWorkflowOutputCrmField[]
  default_fields: IWorkflowOutputCrmField[]
  picklists: IWorkflowOutputCrmPicklist[]
}

export type ISalesforceWorkflowModelFields = IWorkflowModelFields & {
  matching_fields: IWorkflowOutputCrmField[]
}

export type IHubspotWorkflowModelFields = IWorkflowModelFields & {
  matching_fields: IWorkflowOutputCrmField[]
}

export interface IWorkflowOutputHubspot {
  deal: IHubspotWorkflowModelFields & {
    value: number
    stage: string
    currency: string
  }
  company: IHubspotWorkflowModelFields
  contact: IHubspotWorkflowModelFields
  contacts?: IWorkflowOutputContacts
  export_company: boolean
  export_contact: boolean
  export_deal: boolean
  operation_type: EOperationType
}

export interface ISalesforceKeyLabelValue {
  key: string
  label: string
  value: string
}

export interface IWorkflowOutputSalesforceCampaign {
  status: ISalesforceKeyLabelValue
  campaign_id: ISalesforceKeyLabelValue
}

export interface IWorkflowOutputSalesforceTask {
  days_until_due_date?: number
  subject: string
  status: ISalesforceKeyLabelValue
  priority: ISalesforceKeyLabelValue
  owner_id?: ISalesforceKeyLabelValue
}

export interface IWorkflowOutputSalesforce {
  lead: ISalesforceWorkflowModelFields & {
    settings: {
      export_contact_data: boolean
      no_export_if_account_exists: boolean
      no_export_if_contact_exists: boolean
      create_task: boolean
      create_new_task_on_update: boolean
      create_campaign_member: boolean
    }
    task?: IWorkflowOutputSalesforceTask
    campaign_member: IWorkflowOutputSalesforceCampaign
  }
  account: ISalesforceWorkflowModelFields & {
    settings: {
      no_export_if_lead_exists: boolean
      create_task: boolean
      create_new_task_on_update: boolean
      create_campaign_member: boolean
    }
    task?: IWorkflowOutputSalesforceTask
    campaign_member: IWorkflowOutputSalesforceCampaign
  }
  contact: ISalesforceWorkflowModelFields & {
    settings: {
      no_export_if_lead_exists: boolean
      create_campaign_member: boolean
    }
    campaign_member: IWorkflowOutputSalesforceCampaign
  }
  custom_object: ISalesforceWorkflowModelFields & {
    name: string
    lookup_field?: string // name|referenceTo
    settings: {
      create_task: boolean
      create_new_task_on_update: boolean
      export_contact_data: boolean
      lookup_enabled: boolean
    }
    task?: IWorkflowOutputSalesforceTask
  }
  contacts?: IWorkflowOutputContacts
  export_lead: boolean
  export_account: boolean
  export_contact: boolean
  export_custom_object: boolean
  operation_type: EOperationType
  export_contact_data: boolean
}

export interface IWorkflowOutputPipedrive {
  deal: IWorkflowModelFields & {
    stage: number
    value: string
    currency: string
  }
  organization: IWorkflowModelFields
  lead: IWorkflowModelFields
  person: IWorkflowModelFields
  contacts?: IWorkflowOutputContacts
  export_deal: boolean
  export_lead: boolean
  export_person: boolean
  export_organization: boolean
  operation_type?: EOperationType
}

export enum EOutputFormat {
  MicrosoftTeams = 'MicrosoftTeams',
  HangoutChat = 'HangoutChat',
  MatterMost = 'MatterMost',
}

export interface IWorkflowOutputWebhook {
  webhook: {
    url: string
    token: string
  }
  custom_format?: EOutputFormat
  contacts?: IWorkflowOutputContacts
  company_optional_fields?: string[]
  include_security_token?: boolean
  allow_duplicates: boolean
}

export interface IWorkflowOutputZapier {
  contacts?: IWorkflowOutputContacts
}

export enum EHeaderRow {
  Default = 'default',
  None = 'none',
  Blank = 'blank',
}

export interface IWorkflowOutputGoogleSheets {
  spreadsheet: {
    id: string
    tab: string
    name: string
    columns: number
  }
  contacts?: IWorkflowOutputContacts
  custom_fields: IWorkflowOutputCrmField[]
  default_fields: IWorkflowOutputCrmField[]
  access_token_id: number
  header_row?: EHeaderRow
  export_contact: boolean
}

export enum ELinkedinOperationType {
  Add = 'add',
  Remove = 'remove',
}

export interface IWorkflowOutputLinkedin {
  access_token_id: number
  account_id: number
  audience_id: number
  audience_optimization: boolean
  custom_fields: IWorkflowOutputCrmField[]
  operation_type: ELinkedinOperationType
}

type OutreachContactsOutput = Pick<IWorkflowOutputContacts, 'limit' | 'with_emails' | 'with_linkedin_url'> &
  Pick<Contacts.IContactsFilter, 'countries' | 'country_filter_type' | 'export_buyer_persona' | 'buyer_persona_id'>

export interface IWorkflowOutputOutreach {
  campaigns: { user_id: number; campaign_id: string }[]
  contacts: OutreachContactsOutput
  default_fields: IWorkflowOutputCrmField[]
}

export interface IWorkflowOutputClay {
  webhook: {
    url: string
    token: string
  }
  contacts?: IWorkflowOutputContacts
  include_security_token?: boolean
}

export type OutreachConnectionDistribution = 'all' | 'roundRobin'

export interface IWorkflowOutputOutreachConnection {
  distribution: OutreachConnectionDistribution
  campaigns: { user_id: number; campaign_id: string; buyer_persona_id: number; contacts_per_company: number }[]
  default_fields: IWorkflowOutputCrmField[]
}

export interface IWorkflowOutputAttio {
  company: IWorkflowModelFields
  contact: IWorkflowModelFields
  contacts?: IWorkflowOutputContacts
  export_company: boolean
  export_contact: boolean
  operation_type: EOperationType
}

type OutputMap = {
  [EWorkflowTypes.slack]: IWorkflowOutputSlack
  [EWorkflowTypes.email]: IWorkflowOutputEmail
  [EWorkflowTypes.hubspot]: IWorkflowOutputHubspot
  [EWorkflowTypes.salesforce]: IWorkflowOutputSalesforce
  [EWorkflowTypes.pipedrive]: IWorkflowOutputPipedrive
  [EWorkflowTypes.webhook]: IWorkflowOutputWebhook
  [EWorkflowTypes.zapier]: IWorkflowOutputZapier
  [EWorkflowTypes.microsoftTeams]: IWorkflowOutputMicrosoftTeams
  [EWorkflowTypes.googleSheets]: IWorkflowOutputGoogleSheets
  [EWorkflowTypes.linkedin]: IWorkflowOutputLinkedin
  [EWorkflowTypes.outreach]: IWorkflowOutputOutreach
  [EWorkflowTypes.clay]: IWorkflowOutputClay
  [EWorkflowTypes.attio]: IWorkflowOutputAttio
  [EWorkflowTypes.n8n]: IWorkflowOutputN8n
  [OUTREACH_CONNECTION]: IWorkflowOutputOutreachConnection
  [key: string]: any
}

export interface IWorkflowConditions {
  updates?: boolean
  delay_sec?: number
}

export interface IWorfklowConditionEmail extends IWorkflowConditions {
  utc_offset?: string
  delivery_time?: string
  delivery_day?: number
  interval: EDeliveryInterval
}

type ConditionMap = {
  [EWorkflowTypes.email]: IWorfklowConditionEmail
  [key: string]: IWorkflowConditions
}

interface IGenericWorkflow<T extends EWorkflowTypes | OutreachConnection = any> {
  id: number
  customer_id: number
  type: EWorkflowTypes | BomboraConnections | OutreachConnection
  delivery_type: EDeliveryType
  name: string
  segment_id: number
  status: WorkflowStatus
  conditions: ConditionMap[T]
  output: OutputMap[T]
  read_only: boolean
  settings_page: string
  weekly_report_id: number | null
  disabled_by_plan: boolean
  created_by_user_id: number | null
  updated_by_user_id: number | null
  updated_by_user_at: Date | null
  created_at: Date
  updated_at: Date
  deleted_at: Date | null
}

export type IWorkflow = IGenericWorkflow
export type ISalesforceWorkflow = IGenericWorkflow<EWorkflowTypes.salesforce>
export type IHubspotWorkflow = IGenericWorkflow<EWorkflowTypes.hubspot>
export type IZapierWorkflow = IGenericWorkflow<EWorkflowTypes.zapier>
export type IPipedriveWorkflow = IGenericWorkflow<EWorkflowTypes.pipedrive>
export type IEmailWorkflow = IGenericWorkflow<EWorkflowTypes.email>
export type ISlackWorkflow = IGenericWorkflow<EWorkflowTypes.slack>
export type IWebhookWorkflow = IGenericWorkflow<EWorkflowTypes.webhook>
export type IMicrosoftTeamsWorkflow = IGenericWorkflow<EWorkflowTypes.microsoftTeams>
export type IGoogleSheetsWorkflow = IGenericWorkflow<EWorkflowTypes.googleSheets>
export type ILinkedinWorkflow = IGenericWorkflow<EWorkflowTypes.linkedin>
export type IOutreachWorkflow = IGenericWorkflow<EWorkflowTypes.outreach>
export type IClayWorkflow = IGenericWorkflow<EWorkflowTypes.clay>
export type IAttioWorkflow = IGenericWorkflow<EWorkflowTypes.attio>
export type IN8nWorkflow = IGenericWorkflow<EWorkflowTypes.n8n>
export type IOutreachConnectionWorkflow = IGenericWorkflow<OutreachConnection>

export interface IWorkflowOpts {
  type: EWorkflowTypes | BomboraConnections | OutreachConnection
  delivery_type: EDeliveryType
  name: string
  segment_id: number
  status: WorkflowStatus
  conditions?: any
  output?: any
  read_only?: boolean
  settings_page?: string
  disabled_by_plan?: boolean
}

export interface IWorkflowWorkerHistory {
  id: number
  workflow_id: number
  uuid: string
  sent_lead_id: string
  first_seen_at: Date
  sent_at?: Date
}

export interface IWorkflowWorkerHistoryRecordsParams {
  limit: number
  offset: number
}

export interface IWorkflowWorkerHistoryRecords {
  count: number
  historyRecords: IWorkflowWorkerHistory[]
}

type FieldMapperCallbackOpts = {
  lead: IWorkflowBaseLead
  contact?: Contacts.IContact
  outputCrmField: IWorkflowOutputCrmField
  emptyString?: string
}

export type OutputCrmFieldValue = string | number | undefined

export type OutputCrmField = {
  key: string
  albacross_key?: string
  value: OutputCrmFieldValue
  type?: 'field' | 'picklist'
  blank_field_as_match?: boolean
}

export type FieldMapperCallback = (opts: FieldMapperCallbackOpts) => OutputCrmFieldValue

export type IWorkflowFieldMapper = Record<WorkflowFieldName, FieldMapperCallback>
